import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

const ICON_WIDTH = 96;
const ICON_HEIGHT = 108;
const ICON_START_X = 22;
const ICON_START_Y = 40;
const ICON_GAP_X = 106;
const ICON_GAP_Y = 124;
const TASKBAR_HEIGHT = 40;
const BOTTOM_PADDING = 20;

function Icons({
  icons,
  onMouseDown,
  onDoubleClick,
  displayFocus,
  mouse,
  selecting,
  setSelectedIcons,
}) {
  const [iconsRect, setIconsRect] = useState([]);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const rowsPerColumn = useMemo(() => {
    const availableHeight = viewportHeight - TASKBAR_HEIGHT - ICON_START_Y - BOTTOM_PADDING;
    return Math.max(1, Math.floor(availableHeight / ICON_GAP_Y));
  }, [viewportHeight]);

  const arrangedIcons = useMemo(
    () =>
      icons.map((icon, index) => {
        const row = index % rowsPerColumn;
        const column = Math.floor(index / rowsPerColumn);
        return {
          ...icon,
          x: ICON_START_X + column * ICON_GAP_X,
          y: ICON_START_Y + row * ICON_GAP_Y,
        };
      }),
    [icons, rowsPerColumn],
  );

  const measure = useCallback(rect => {
    setIconsRect(prev => {
      const existed = prev.find(item => item.id === rect.id);
      if (
        existed &&
        existed.x === rect.x &&
        existed.y === rect.y &&
        existed.w === rect.w &&
        existed.h === rect.h
      ) {
        return prev;
      }
      const next = prev.filter(item => item.id !== rect.id);
      next.push(rect);
      return next;
    });
  }, []);

  useEffect(() => {
    function onResize() {
      setViewportHeight(window.innerHeight);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!selecting) return;
    const sx = Math.min(selecting.x, mouse.docX);
    const sy = Math.min(selecting.y, mouse.docY);
    const sw = Math.abs(selecting.x - mouse.docX);
    const sh = Math.abs(selecting.y - mouse.docY);

    const selectedIds = iconsRect
      .filter(rect => {
        const { x, y, w, h } = rect;
        return x - sx < sw && sx - x < w && y - sy < sh && sy - y < h;
      })
      .map(icon => icon.id);

    setSelectedIcons(selectedIds);
  }, [iconsRect, setSelectedIcons, selecting, mouse.docX, mouse.docY]);

  return (
    <IconsContainer>
      {arrangedIcons.map(icon => (
        <StyledIcon
          key={icon.id}
          {...icon}
          displayFocus={displayFocus}
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
          measure={measure}
        />
      ))}
    </IconsContainer>
  );
}

function Icon({
  title,
  onMouseDown,
  onDoubleClick,
  icon,
  className,
  id,
  measure,
  x,
  y,
  ...iconAction
}) {
  const ref = useRef(null);

  function handleMouseDown(e) {
    e.stopPropagation();
    onMouseDown(id);
  }

  function handleDoubleClick(e) {
    e.stopPropagation();
    onDoubleClick(iconAction);
  }

  useEffect(() => {
    const target = ref.current;
    if (!target) return;

    const update = () => {
      const { left, top, width, height } = target.getBoundingClientRect();
      const posX = left + window.scrollX;
      const posY = top + window.scrollY;
      measure({ id, x: posX, y: posY, w: width, h: height });
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [id, measure, x, y]);

  return (
    <div
      className={className}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      ref={ref}
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <div className={`${className}__img__container`}>
        <img src={icon} alt={title} className={`${className}__img`} draggable={false} />
      </div>
      <div className={`${className}__text__container`}>
        <div className={`${className}__text`}>{title}</div>
      </div>
    </div>
  );
}

const IconsContainer = styled.div`
  position: absolute;
  inset: 0;
`;

const StyledIcon = styled(Icon)`
  width: ${ICON_WIDTH}px;
  height: ${ICON_HEIGHT}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
  position: absolute;
  &__text__container {
    width: 100%;
    font-size: 14px;
    color: white;
    text-shadow: 0 1px 1px black;
    margin-top: 5px;
    display: flex;
    justify-content: center;

    &:before {
      content: '';
      display: block;
      flex-grow: 1;
    }
    &:after {
      content: '';
      display: block;
      flex-grow: 1;
    }
  }
  &__text {
    padding: 0 3px 2px;
    background-color: ${({ isFocus, displayFocus }) =>
      isFocus && displayFocus ? '#0b61ff' : 'transparent'};
    text-align: center;
    flex-shrink: 1;
    word-break: break-word;
  }
  &__img__container {
    width: 52px;
    height: 52px;
    filter: ${({ isFocus, displayFocus }) =>
      isFocus && displayFocus ? 'drop-shadow(0 0 blue)' : ''};
  }
  &__img {
    width: 100%;
    height: 100%;
    opacity: ${({ isFocus, displayFocus }) => (isFocus && displayFocus ? 0.5 : 1)};
  }
`;

export default Icons;
